import {
  EGRESS_STRATEGY_VALUES,
  type EgressStrategy,
  type ExternalNetworkDto,
  type ScanProfileDto,
  type ScanProfileTier,
  type UpdateExternalNetworkDto,
} from '@terra/shared-contracts-external-network';
import { createSelectListCollection } from '../../../components/select';
import {
  DEFAULT_SCANNING_SCHEDULE,
  type ScanningConfigurationChange,
} from '../../settings/automation/ScanningConfigurationCard';

export type ScanProfileTierLabels = Record<ScanProfileTier, string>;

export type ExternalNetworkSettingsPersistRequest = {
  updates: UpdateExternalNetworkDto;
  showSuccessToast?: boolean;
};

export type ConnectorDnsNetwork = {
  id: string;
  splitDns?: { nameservers?: readonly string[] } | null;
};

export type ExternalNetworkSettingsSelectItem = {
  label: string;
  value: string;
};

export const hasExternalNetworkSchedule = (
  schedule:
    | ExternalNetworkDto['schedule']
    | ExternalNetworkDto['executionSchedule']
    | undefined,
): boolean => schedule !== null && schedule !== undefined;

export const getScanProfileDisplayLabel = ({
  profile,
  tierLabels,
  fallbackName,
}: {
  profile?: Pick<ScanProfileDto, 'name' | 'systemTier'> | null;
  tierLabels: ScanProfileTierLabels;
  fallbackName?: string;
}): string => {
  if (profile?.systemTier) {
    return tierLabels[profile.systemTier];
  }

  if (profile?.name) {
    return profile.name;
  }

  return fallbackName ?? '';
};

export const createScanProfileCollection = ({
  profiles,
  tierLabels,
}: {
  profiles: readonly Pick<ScanProfileDto, 'id' | 'name' | 'systemTier'>[];
  tierLabels: ScanProfileTierLabels;
}) =>
  createSelectListCollection({
    items: profiles.map((profile) => ({
      label: getScanProfileDisplayLabel({ profile, tierLabels }),
      value: profile.id,
    })),
  });

export const formatEgressStrategyLabel = (strategy: EgressStrategy): string =>
  strategy
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');

export const getEgressStrategyOptions = ({
  isConnectorScanEnabled,
}: {
  isConnectorScanEnabled: boolean;
}): ExternalNetworkSettingsSelectItem[] =>
  EGRESS_STRATEGY_VALUES.filter(
    (strategy) => isConnectorScanEnabled || strategy !== 'CONNECTOR',
  ).map((strategy) => ({
    label: formatEgressStrategyLabel(strategy),
    value: strategy,
  }));

export const isEgressStrategy = (value: string): value is EgressStrategy =>
  EGRESS_STRATEGY_VALUES.some((strategy) => strategy === value);

export const resolveConnectorDnsServerIps = ({
  networks,
  networkId,
}: {
  networks: readonly ConnectorDnsNetwork[];
  networkId: string | null;
}): string[] => [
  ...(networks.find((network) => network.id === networkId)?.splitDns
    ?.nameservers ?? []),
];

export const buildEgressStrategyUpdates = ({
  egressStrategy,
  internalAccessNetworkId,
  dnsServerIps,
}: {
  egressStrategy: EgressStrategy;
  internalAccessNetworkId: string | null;
  dnsServerIps: string[];
}): UpdateExternalNetworkDto => ({
  egressStrategy,
  ...(egressStrategy === 'CONNECTOR' && internalAccessNetworkId
    ? { internalAccessNetworkId }
    : {}),
  ...(egressStrategy === 'CONNECTOR' ? { dnsServerIps } : {}),
});

export const resolveDiscoveryScheduleUpdates = ({
  change,
  limitScheduleEnabled,
}: {
  change: ScanningConfigurationChange;
  limitScheduleEnabled: boolean;
}): UpdateExternalNetworkDto | null => {
  if (typeof change.limitScheduleEnabled === 'boolean') {
    return {
      schedule: change.limitScheduleEnabled
        ? (change.automationSchedule ?? DEFAULT_SCANNING_SCHEDULE)
        : null,
    };
  }

  if (change.automationSchedule !== undefined && limitScheduleEnabled) {
    return { schedule: change.automationSchedule };
  }

  return null;
};

export const resolveExecutionScheduleUpdates = ({
  change,
  limitScheduleEnabled,
}: {
  change: ScanningConfigurationChange;
  limitScheduleEnabled: boolean;
}): UpdateExternalNetworkDto | null => {
  if (typeof change.smokeTestEnabled === 'boolean') {
    return { smokeTestEnabled: change.smokeTestEnabled };
  }

  if (typeof change.limitScheduleEnabled === 'boolean') {
    return {
      executionSchedule: change.limitScheduleEnabled
        ? (change.automationSchedule ?? DEFAULT_SCANNING_SCHEDULE)
        : null,
    };
  }

  if (change.automationSchedule !== undefined && limitScheduleEnabled) {
    return { executionSchedule: change.automationSchedule };
  }

  return null;
};
